import { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Loader2, Sparkles, AlertTriangle, CheckCircle2, ChevronRight,
  Wand2, ShieldCheck, ClipboardList,
} from 'lucide-react';
import { api } from '../../lib/api';
import DiffView from './DiffView';

// ── Typen (spiegeln das Backend) ────────────────────────────────
interface Finding {
  cardId: string;
  severity: 'hoch' | 'mittel' | 'gering';
  category: string;
  title: string;
  detail: string;
  recommendation: string;
}
interface AnalysisResult {
  overall: { foerderfaehig: 'ja' | 'teilweise' | 'nein'; score: number; summary: string };
  findings: Finding[];
}
interface Proposal {
  cardId: string;
  label: string;
  beforeText: string;
  afterText: string;
  changeSummary: string;
  ok: boolean;
  reason?: string;
}
interface RevisionJob {
  id: number;
  kind: 'analyze' | 'reconstruct';
  status: 'running' | 'done' | 'error' | 'cancelled';
  total: number;
  done: number;
  currentLabel: string | null;
  error: string | null;
  consumedAt: string | null;
  analysis: AnalysisResult | null;
  proposals: Proposal[];
}

// Narrative Blöcke (analysierbar/überarbeitbar) — Labels für Anzeige & manuelle Auswahl.
const BLOCK_LABELS: Record<string, string> = {
  'card-1-1': '1.1 Beratungsauftrag und Grund / Anlass',
  'card-1-4': '1.4 Geschäftsgegenstand',
  'card-2-1': '2.1 Ist-Zustand',
  'card-2-2': '2.2 Stärken und Potenziale',
  'card-2-3': '2.3 Schwächen und Handlungsbedarfe',
  execution: '3. Auftragsdurchführung & Vorgehen',
  'card-4-1': '4.1 Ermittelte Schwachstellen',
  'card-4-2': '4.2 Fragestellungen zur Strukturierung',
  'action-plan': '5. Detaillierter Maßnahmeplan',
};
const NARRATIVE_ORDER = Object.keys(BLOCK_LABELS);
const blockLabel = (cardId: string) => BLOCK_LABELS[cardId] || cardId;

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

type Step = 'intro' | 'analyzing' | 'findings' | 'reconstructing' | 'review';

interface Props {
  open: boolean;
  onClose: () => void;
  reportId: number | null;
  darkMode: boolean;
  /** Wird nach erfolgreicher Übernahme aufgerufen — der Editor aktualisiert seinen State. */
  onAccepted: (items: Array<{ cardId: string; text: string }>) => void;
}

export default function ReportAnalysisModal({ open, onClose, reportId, darkMode, onAccepted }: Props) {
  const [step, setStep] = useState<Step>('intro');
  const [note, setNote] = useState('');
  const [extra, setExtra] = useState('');
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [manualBlocks, setManualBlocks] = useState<Set<string>>(new Set());
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [acceptedCards, setAcceptedCards] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<{ done: number; total: number; label: string }>({ done: 0, total: 0, label: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const stopRef = useRef(false);

  const resetAll = useCallback(() => {
    setStep('intro'); setNote(''); setExtra(''); setAnalysis(null);
    setSelected(new Set()); setManualBlocks(new Set()); setProposals([]);
    setAcceptedCards(new Set()); setProgress({ done: 0, total: 0, label: '' });
    setError(null); setBusy(false);
  }, []);

  const pollStatus = useCallback(async (): Promise<RevisionJob | null> => {
    if (!reportId) return null;
    const res = await api.get<{ job: RevisionJob | null }>(`/api/reports/${reportId}/revision/status`);
    return res.job;
  }, [reportId]);

  const loopPoll = useCallback(async (expectedKind: 'analyze' | 'reconstruct', onDone: (job: RevisionJob) => void) => {
    for (;;) {
      if (stopRef.current) return;
      let job: RevisionJob | null = null;
      try { job = await pollStatus(); } catch { await sleep(2000); continue; }
      if (job && job.kind === expectedKind) {
        if (job.status === 'running') {
          setProgress({ done: job.done, total: job.total, label: job.currentLabel || '' });
        } else {
          onDone(job);
          return;
        }
      }
      await sleep(2000);
    }
  }, [pollStatus]);

  // Beim Öffnen: zurücksetzen; falls serverseitig bereits ein Job läuft, dort wieder andocken.
  useEffect(() => {
    if (!open) { stopRef.current = true; return; }
    stopRef.current = false;
    resetAll();
    (async () => {
      try {
        const job = await pollStatus();
        if (job && job.status === 'running') {
          if (job.kind === 'analyze') {
            setStep('analyzing');
            setProgress({ done: job.done, total: job.total, label: job.currentLabel || '' });
            loopPoll('analyze', handleAnalyzeDone);
          } else {
            setStep('reconstructing');
            setProgress({ done: job.done, total: job.total, label: job.currentLabel || '' });
            loopPoll('reconstruct', handleReconstructDone);
          }
        }
      } catch { /* frischer Start */ }
    })();
    return () => { stopRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Phase 1: Analyse ──────────────────────────────────────────
  const handleAnalyzeDone = (job: RevisionJob) => {
    if (job.status === 'done' && job.analysis) {
      setAnalysis(job.analysis);
      const pre = new Set<number>();
      job.analysis.findings.forEach((f, i) => { if (f.severity !== 'gering' && f.cardId) pre.add(i); });
      setSelected(pre);
      setStep('findings');
    } else {
      setError(job.error || 'Analyse fehlgeschlagen.');
      setStep('intro');
    }
  };

  const startAnalysis = async () => {
    if (!reportId) return;
    setError(null);
    setStep('analyzing');
    setProgress({ done: 0, total: 1, label: 'Bericht wird analysiert…' });
    try {
      await api.post(`/api/reports/${reportId}/revision/analyze`, { note: note.trim() || undefined });
      await loopPoll('analyze', handleAnalyzeDone);
    } catch (e: any) {
      setError(e?.message || 'Analyse konnte nicht gestartet werden.');
      setStep('intro');
    }
  };

  // ── Phase 2: Überarbeitung ────────────────────────────────────
  const buildTargets = (): Array<{ cardId: string; label: string; instructions: string }> => {
    const instrByCard: Record<string, string[]> = {};
    const included = new Set<string>();
    if (analysis) {
      analysis.findings.forEach((f, i) => {
        if (selected.has(i) && f.cardId) {
          included.add(f.cardId);
          (instrByCard[f.cardId] = instrByCard[f.cardId] || []).push(f.recommendation);
        }
      });
    }
    manualBlocks.forEach((c) => included.add(c));
    const ex = extra.trim();
    return [...included]
      .map((cardId) => {
        const parts = (instrByCard[cardId] || []).map((r) => '• ' + r);
        if (ex) parts.push('• ' + ex);
        return { cardId, label: blockLabel(cardId), instructions: parts.join('\n') };
      })
      .filter((t) => t.instructions.trim())
      .sort((a, b) => NARRATIVE_ORDER.indexOf(a.cardId) - NARRATIVE_ORDER.indexOf(b.cardId));
  };

  const handleReconstructDone = (job: RevisionJob) => {
    setProposals(job.proposals || []);
    if (job.status === 'error' && !(job.proposals || []).length) {
      setError(job.error || 'Überarbeitung fehlgeschlagen.');
      setStep('findings');
    } else {
      setStep('review');
    }
  };

  const startReconstruct = async () => {
    if (!reportId) return;
    const targets = buildTargets();
    if (!targets.length) {
      setError('Bitte mindestens einen Punkt auswählen oder einen Block samt Anweisung angeben.');
      return;
    }
    setError(null);
    setStep('reconstructing');
    setProgress({ done: 0, total: targets.length, label: '' });
    try {
      await api.post(`/api/reports/${reportId}/revision/reconstruct`, { targets });
      await loopPoll('reconstruct', handleReconstructDone);
    } catch (e: any) {
      setError(e?.message || 'Überarbeitung konnte nicht gestartet werden.');
      setStep('findings');
    }
  };

  // ── Übernahme ─────────────────────────────────────────────────
  const acceptItems = async (cards: Proposal[]) => {
    if (!reportId) return;
    const items = cards
      .filter((p) => p.ok && p.afterText && !acceptedCards.has(p.cardId))
      .map((p) => ({ cardId: p.cardId, text: p.afterText }));
    if (!items.length) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/reports/${reportId}/revision/accept`, { items });
      const next = new Set(acceptedCards);
      items.forEach((it) => next.add(it.cardId));
      setAcceptedCards(next);
      onAccepted(items);
    } catch (e: any) {
      setError(e?.message || 'Übernahme fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const cancelRunning = async () => {
    if (!reportId) return;
    stopRef.current = true;
    try { await api.post(`/api/reports/${reportId}/revision/cancel`, {}); } catch { /* ignore */ }
    stopRef.current = false;
    setStep(analysis ? 'findings' : 'intro');
  };

  const close = () => { stopRef.current = true; onClose(); };

  if (!open) return null;

  // ── Styling-Helfer ────────────────────────────────────────────
  const panel = darkMode ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-slate-900';
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';
  const subtleBox = darkMode ? 'bg-slate-900/50 border-slate-700' : 'bg-slate-50 border-slate-200';
  const inputCls = darkMode
    ? 'bg-slate-900 border-slate-600 text-slate-100 placeholder-slate-500'
    : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400';

  const sevBadge = (s: Finding['severity']) =>
    s === 'hoch'
      ? (darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700')
      : s === 'mittel'
      ? (darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700')
      : (darkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600');

  const ffBadge = (f: AnalysisResult['overall']['foerderfaehig']) =>
    f === 'ja'
      ? (darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-700')
      : f === 'teilweise'
      ? (darkMode ? 'bg-amber-900/40 text-amber-300' : 'bg-amber-100 text-amber-700')
      : (darkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700');
  const ffLabel = { ja: 'förderfähig', teilweise: 'teilweise förderfähig', nein: 'nicht förderfähig' };

  const okProposals = proposals.filter((p) => p.ok && p.afterText);
  const allAccepted = okProposals.length > 0 && okProposals.every((p) => acceptedCards.has(p.cardId));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-3 sm:p-4" onClick={close}>
      <div
        className={`w-full max-w-4xl max-h-[92vh] flex flex-col rounded-xl border shadow-xl ${panel}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-5 h-5 text-blue-500 shrink-0" />
            <h2 className="text-base font-bold truncate">Bericht analysieren & optimieren</h2>
          </div>
          <button onClick={close} className={`p-1.5 rounded-lg shrink-0 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-slate-100'}`} title="Schließen">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4">
          {error && (
            <div className={`mb-4 flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${darkMode ? 'bg-red-900/20 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* ── Schritt: Intro ── */}
          {step === 'intro' && (
            <div className="space-y-4">
              <p className={`text-sm ${muted}`}>
                Die KI bewertet den gesamten Bericht auf Qualität und <strong>Förderfähigkeit</strong> und schlägt
                konkrete Verbesserungen vor. Anschließend wählst du, was überarbeitet werden soll, und übernimmst
                die Vorschläge blockweise.
              </p>
              <div>
                <label className="block text-sm font-medium mb-1.5">Optional: Was stört dich am Bericht?</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="z. B.: Schwachstellen zu allgemein, Maßnahmen zu knapp. Fließt als Schwerpunkt in die Analyse ein."
                  className={`w-full rounded-lg border px-3 py-2 text-sm resize-y ${inputCls}`}
                />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={startAnalysis}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
                >
                  <Sparkles className="w-4 h-4" /> Analyse starten
                </button>
              </div>
            </div>
          )}

          {/* ── Schritt: Analyse läuft ── */}
          {step === 'analyzing' && (
            <div className="py-12 flex flex-col items-center text-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              <p className="text-sm font-medium">{progress.label || 'Bericht wird analysiert…'}</p>
              <p className={`text-xs ${muted}`}>Das kann einen Moment dauern (gründliche Gesamtbewertung).</p>
              <button onClick={cancelRunning} className={`mt-2 text-xs underline ${muted}`}>Abbrechen</button>
            </div>
          )}

          {/* ── Schritt: Findings + Auswahl ── */}
          {step === 'findings' && analysis && (
            <div className="space-y-4">
              {/* Gesamteinschätzung */}
              <div className={`rounded-lg border p-3 ${subtleBox}`}>
                <div className="flex items-center flex-wrap gap-2 mb-2">
                  <ShieldCheck className="w-4 h-4 text-blue-500" />
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ffBadge(analysis.overall.foerderfaehig)}`}>
                    {ffLabel[analysis.overall.foerderfaehig]}
                  </span>
                  <span className={`text-xs ${muted}`}>Qualität: <strong>{analysis.overall.score}/100</strong></span>
                </div>
                <p className="text-sm leading-relaxed">{analysis.overall.summary}</p>
              </div>

              {/* Findings-Checkliste */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <ClipboardList className="w-4 h-4 text-blue-500" />
                  <h3 className="text-sm font-semibold">Vorgeschlagene Verbesserungen ({analysis.findings.length})</h3>
                </div>
                {analysis.findings.length === 0 && (
                  <p className={`text-sm ${muted}`}>Keine konkreten Mängel gefunden — der Bericht wirkt vollständig.</p>
                )}
                <ul className="space-y-2">
                  {analysis.findings.map((f, i) => {
                    const checked = selected.has(i);
                    return (
                      <li key={i}>
                        <label className={`flex gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${checked ? (darkMode ? 'border-blue-600 bg-blue-900/20' : 'border-blue-400 bg-blue-50') : (darkMode ? 'border-slate-700 hover:border-slate-600' : 'border-slate-200 hover:border-slate-300')}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const next = new Set(selected);
                              checked ? next.delete(i) : next.add(i);
                              setSelected(next);
                            }}
                            className="mt-0.5 w-4 h-4 shrink-0 accent-blue-600"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center flex-wrap gap-2">
                              <span className={`text-[12px] font-semibold uppercase px-1.5 py-0.5 rounded ${sevBadge(f.severity)}`}>{f.severity}</span>
                              <span className={`text-[13px] ${muted}`}>{blockLabel(f.cardId)} · {f.category}</span>
                            </div>
                            <p className="text-sm font-medium mt-1">{f.title}</p>
                            <p className={`text-xs mt-0.5 ${muted}`}>{f.detail}</p>
                            <p className="text-xs mt-1 flex items-start gap-1"><Wand2 className="w-3 h-3 mt-0.5 shrink-0 text-blue-500" /><span>{f.recommendation}</span></p>
                          </div>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>

              {/* Erweiterte eigene Anweisungen */}
              <div>
                <label className="block text-sm font-medium mb-1.5">Eigene Anweisungen (erweitert, optional)</label>
                <textarea
                  value={extra}
                  onChange={(e) => setExtra(e.target.value)}
                  rows={2}
                  placeholder="Zusätzliche Wünsche — werden auf die ausgewählten/markierten Blöcke angewendet."
                  className={`w-full rounded-lg border px-3 py-2 text-sm resize-y ${inputCls}`}
                />
              </div>

              {/* Manuelle Block-Auswahl */}
              <div>
                <p className={`text-xs mb-1.5 ${muted}`}>Weitere Blöcke direkt überarbeiten (auch ohne Finding):</p>
                <div className="flex flex-wrap gap-1.5">
                  {NARRATIVE_ORDER.map((cardId) => {
                    const on = manualBlocks.has(cardId);
                    return (
                      <button
                        key={cardId}
                        type="button"
                        onClick={() => {
                          const next = new Set(manualBlocks);
                          on ? next.delete(cardId) : next.add(cardId);
                          setManualBlocks(next);
                        }}
                        className={`text-[13px] px-2 py-1 rounded-full border transition-colors ${on ? 'bg-blue-600 border-blue-600 text-white' : (darkMode ? 'border-slate-600 text-slate-300 hover:border-slate-500' : 'border-slate-300 text-slate-600 hover:border-slate-400')}`}
                      >
                        {blockLabel(cardId)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ── Schritt: Überarbeitung läuft ── */}
          {step === 'reconstructing' && (
            <div className="py-12 flex flex-col items-center text-center gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              <p className="text-sm font-medium">{progress.label ? `Überarbeite: ${progress.label}` : 'Überarbeitung läuft…'}</p>
              <div className={`h-1.5 w-56 rounded-full overflow-hidden ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                <div className="h-full bg-blue-600 transition-all duration-300" style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }} />
              </div>
              <p className={`text-xs ${muted}`}>{progress.done}/{progress.total} Blöcke</p>
              <button onClick={cancelRunning} className={`mt-1 text-xs underline ${muted}`}>Abbrechen</button>
            </div>
          )}

          {/* ── Schritt: Review / Diff ── */}
          {step === 'review' && (
            <div className="space-y-4">
              <p className={`text-sm ${muted}`}>
                Vorher/Nachher je Block. Übernimm einzeln oder alle auf einmal — erst dann wird der Bericht geändert.
              </p>
              {proposals.map((p) => (
                <div key={p.cardId} className={`rounded-lg border p-3 ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                    <span className="text-sm font-semibold">{p.label}</span>
                    {p.ok && p.afterText ? (
                      acceptedCards.has(p.cardId) ? (
                        <span className={`inline-flex items-center gap-1 text-xs font-medium ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                          <CheckCircle2 className="w-4 h-4" /> übernommen
                        </span>
                      ) : (
                        <button
                          onClick={() => acceptItems([p])}
                          disabled={busy}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-xs font-semibold"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Übernehmen
                        </button>
                      )
                    ) : (
                      <span className={`inline-flex items-center gap-1 text-xs ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                        <AlertTriangle className="w-3.5 h-3.5" /> {p.reason || 'kein Vorschlag'}
                      </span>
                    )}
                  </div>
                  {p.ok && p.afterText && <DiffView before={p.beforeText} after={p.afterText} darkMode={darkMode} />}
                </div>
              ))}
              {okProposals.length === 0 && (
                <p className={`text-sm ${muted}`}>Keine übernehmbaren Vorschläge erzeugt.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer-Aktionen (Findings & Review) */}
        {(step === 'findings' || step === 'review') && (
          <div className={`flex items-center justify-between gap-2 px-4 sm:px-5 py-3 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            {step === 'findings' ? (
              <>
                <button onClick={close} className={`text-sm ${muted} hover:underline`}>Schließen</button>
                <button
                  onClick={startReconstruct}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
                >
                  Überarbeitung planen <ChevronRight className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setStep('findings')} className={`text-sm ${muted} hover:underline`}>← Zurück zur Auswahl</button>
                <div className="flex items-center gap-2">
                  <button onClick={close} className={`text-sm px-3 py-2 ${muted} hover:underline`}>Fertig</button>
                  <button
                    onClick={() => acceptItems(okProposals)}
                    disabled={busy || allAccepted || okProposals.length === 0}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    {allAccepted ? 'Alle übernommen' : 'Alle übernehmen'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
